import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
    FiArrowLeft,
    FiEdit2,
    FiPlusCircle,
    FiAlertTriangle,
    FiPackage,
    FiTrash2,
} from "react-icons/fi";
import inventoryService from "../../services/inventoryService";
import ConfirmModal from "../../components/common/ConfirmModal";

const TX_CONFIG = {
    "Stock In": { color: "#16a34a", bg: "#dcfce7", sign: "+" },
    "Stock Out": { color: "#dc2626", bg: "#fee2e2", sign: "-" },
    Return: { color: "#003b67", bg: "#e8f1f8", sign: "+" },
    Adjustment: { color: "#d97706", bg: "#fef3c7", sign: "±" },
};

export default function ItemDetail() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [item, setItem] = useState(null);
    const [loading, setLoading] = useState(true);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const fetchItem = useCallback(() => {
        inventoryService
            .getItemById(id)
            .then((res) => setItem(res.data))
            .catch((err) => {
                toast.error(err.message);
                navigate("/inventory/items");
            })
            .finally(() => setLoading(false));
    }, [id, navigate]);

    useEffect(() => {
        fetchItem();
    }, [fetchItem]);

    const handleDeleteTx = async () => {
        setDeleting(true);
        try {
            const res = await inventoryService.deleteTransaction(deleteTarget._id);
            toast.success(res.message);
            setDeleteTarget(null);
            fetchItem();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setDeleting(false);
        }
    };

    if (loading)
        return (
            <div className="loading-center">
                <div className="spinner" />
                Loading item…
            </div>
        );
    if (!item) return null;

    const isLow =
        item.minStockLevel > 0 && item.currentStock <= item.minStockLevel;
    const color = item.category?.color || "#6b7280";

    return (
        <div>
            {/* ── Header ── */}
            <div className="page-header" style={{ flexWrap: "wrap", gap: "0.5rem" }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: "1rem" }}>
                    <div
                        style={{
                            width: 48,
                            height: 48,
                            borderRadius: 14,
                            background: `${color}18`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                        }}
                    >
                        <FiPackage size={22} color={color} />
                    </div>
                    <div>
                        <h1 className="page-title" style={{ marginBottom: 4 }}>
                            {item.name}
                            {isLow && (
                                <FiAlertTriangle
                                    size={16}
                                    color="#dc2626"
                                    style={{ marginLeft: 8 }}
                                    title="Low stock!"
                                />
                            )}
                        </h1>
                        <div
                            style={{
                                display: "flex",
                                gap: "0.5rem",
                                alignItems: "center",
                                flexWrap: "wrap",
                            }}
                        >
                            <span
                                style={{
                                    background: `${color}18`,
                                    color,
                                    padding: "2px 9px",
                                    borderRadius: 99,
                                    fontSize: 12,
                                    fontWeight: 700,
                                }}
                            >
                                {item.category?.name}
                            </span>
                            {item.academicYear?.label && (
                                <span className="badge badge-purple" style={{ fontSize: 12 }}>
                                    AY: {item.academicYear.label}
                                </span>
                            )}
                            {item.itemCode && (
                                <span className="badge badge-blue">{item.itemCode}</span>
                            )}
                            {item.location && (
                                <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                                    📍 {item.location}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                    <button
                        className="btn btn-outline"
                        onClick={() => navigate("/inventory/items")}
                    >
                        <FiArrowLeft size={14} /> Back
                    </button>
                    <button
                        className="btn btn-outline"
                        onClick={() => navigate(`/inventory/items/${id}/edit`)}
                    >
                        <FiEdit2 size={14} /> Edit
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={() => navigate(`/inventory/transactions/new?item=${id}`)}
                    >
                        <FiPlusCircle size={14} /> New Transaction
                    </button>
                </div>
            </div>

            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 260px",
                    gap: "1rem",
                    alignItems: "start",
                }}
            >
                {/* ── Transaction History ── */}
                <div className="card">
                    <div className="card-header">Transaction History</div>
                    {!item.recentTransactions?.length ? (
                        <div
                            className="card-body"
                            style={{
                                textAlign: "center",
                                color: "var(--text-muted)",
                                padding: "2rem",
                            }}
                        >
                            No transactions yet.
                            <div style={{ marginTop: "0.75rem" }}>
                                <button
                                    className="btn btn-primary btn-sm"
                                    onClick={() =>
                                        navigate(`/inventory/transactions/new?item=${id}`)
                                    }
                                >
                                    <FiPlusCircle size={12} /> Add Stock In
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="table-wrapper">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th style={{ textAlign: "center" }}>Type</th>
                                        <th style={{ textAlign: "center" }}>Qty</th>
                                        <th style={{ textAlign: "center" }}>Before → After</th>
                                        <th>Details</th>
                                        <th />
                                    </tr>
                                </thead>
                                <tbody>
                                    {item.recentTransactions.map((tx) => {
                                        const tc = TX_CONFIG[tx.type] || TX_CONFIG["Adjustment"];
                                        return (
                                            <tr key={tx._id}>
                                                <td
                                                    style={{ fontSize: 12, color: "var(--text-muted)" }}
                                                >
                                                    {tx.transactionDate}
                                                </td>
                                                <td style={{ textAlign: "center" }}>
                                                    <span
                                                        style={{
                                                            ...tc,
                                                            padding: "2px 8px",
                                                            borderRadius: 99,
                                                            fontSize: 11.5,
                                                            fontWeight: 600,
                                                        }}
                                                    >
                                                        {tx.type}
                                                    </span>
                                                </td>
                                                <td
                                                    style={{
                                                        textAlign: "center",
                                                        fontWeight: 700,
                                                        color: tc.color,
                                                        fontSize: 13,
                                                    }}
                                                >
                                                    {tc.sign}
                                                    {tx.quantity} {item.unit}
                                                </td>
                                                <td
                                                    style={{
                                                        textAlign: "center",
                                                        fontSize: 12,
                                                        color: "var(--text-muted)",
                                                    }}
                                                >
                                                    {tx.stockBefore} →{" "}
                                                    <strong style={{ color: "var(--text)" }}>
                                                        {tx.stockAfter}
                                                    </strong>
                                                </td>
                                                <td style={{ fontSize: 12 }}>
                                                    {tx.issuedToName && <div>{tx.issuedToName}</div>}
                                                    {tx.supplier?.name && (
                                                        <div style={{ color: "var(--text-muted)" }}>
                                                            {tx.supplier.name}
                                                        </div>
                                                    )}
                                                    {tx.invoiceNo && (
                                                        <div style={{ color: "var(--text-muted)" }}>
                                                            Inv: {tx.invoiceNo}
                                                        </div>
                                                    )}
                                                    {tx.adjustmentReason && (
                                                        <div style={{ color: "var(--text-muted)" }}>
                                                            {tx.adjustmentReason}
                                                        </div>
                                                    )}
                                                    {tx.remarks && (
                                                        <div
                                                            style={{
                                                                color: "var(--text-muted)",
                                                                fontStyle: "italic",
                                                            }}
                                                        >
                                                            {tx.remarks}
                                                        </div>
                                                    )}
                                                </td>
                                                <td>
                                                    <button
                                                        className="btn btn-danger btn-sm"
                                                        onClick={() => setDeleteTarget(tx)}
                                                        title="Delete & reverse"
                                                    >
                                                        <FiTrash2 size={11} />
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* ── Stock info panel ── */}
                <div style={{ display: "grid", gap: "1rem" }}>
                    {/* Stock card */}
                    <div
                        className="card"
                        style={{
                            padding: "1.25rem",
                            borderLeft: `4px solid ${isLow ? "#dc2626" : "#16a34a"}`,
                        }}
                    >
                        <div
                            style={{
                                fontSize: 11,
                                textTransform: "uppercase",
                                letterSpacing: "0.06em",
                                color: "var(--text-muted)",
                                fontWeight: 700,
                                marginBottom: 6,
                            }}
                        >
                            Current Stock
                        </div>
                        <div
                            style={{
                                fontSize: 36,
                                fontWeight: 900,
                                color: isLow ? "#dc2626" : "#16a34a",
                                lineHeight: 1,
                            }}
                        >
                            {item.currentStock}
                        </div>
                        <div
                            style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 4 }}
                        >
                            {item.unit}
                        </div>
                        {item.minStockLevel > 0 && (
                            <div
                                style={{
                                    marginTop: "0.75rem",
                                    fontSize: 12.5,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 5,
                                    color: isLow ? "#dc2626" : "var(--text-muted)",
                                }}
                            >
                                {isLow && <FiAlertTriangle size={12} />}
                                Min level: {item.minStockLevel} {item.unit}
                            </div>
                        )}
                        {item.unitPrice > 0 && (
                            <div
                                style={{
                                    marginTop: 4,
                                    fontSize: 12,
                                    color: "var(--text-muted)",
                                }}
                            >
                                Value: ₹{" "}
                                {(item.currentStock * item.unitPrice).toLocaleString("en-IN")}
                            </div>
                        )}
                    </div>

                    {/* Details */}
                    <div className="card">
                        <div className="card-header" style={{ fontSize: 13 }}>
                            Item Info
                        </div>
                        <div
                            style={{
                                padding: "0.85rem 1.1rem",
                                display: "grid",
                                gap: "0.5rem",
                            }}
                        >
                            {[
                                ["Unit", item.unit],
                                [
                                    "Unit Price",
                                    item.unitPrice > 0
                                        ? `₹ ${item.unitPrice.toLocaleString("en-IN")}`
                                        : "—",
                                ],
                                ["Location", item.location || "—"],
                            ].map(([label, val]) => (
                                <div
                                    key={label}
                                    style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        fontSize: 13,
                                        padding: "4px 0",
                                        borderBottom: "1px solid var(--border)",
                                    }}
                                >
                                    <span style={{ color: "var(--text-muted)" }}>{label}</span>
                                    <span style={{ fontWeight: 600 }}>{val}</span>
                                </div>
                            ))}
                            {item.description && (
                                <div
                                    style={{
                                        fontSize: 12.5,
                                        color: "var(--text-muted)",
                                        paddingTop: 6,
                                        lineHeight: 1.5,
                                    }}
                                >
                                    {item.description}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Quick transaction shortcuts */}
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: "0.5rem",
                        }}
                    >
                        {["Stock In", "Stock Out", "Return", "Adjustment"].map((type) => {
                            const tc = TX_CONFIG[type];
                            return (
                                <button
                                    key={type}
                                    className="btn btn-outline btn-sm"
                                    style={{
                                        justifyContent: "center",
                                        borderColor: tc.color,
                                        color: tc.color,
                                    }}
                                    onClick={() =>
                                        navigate(
                                            `/inventory/transactions/new?item=${id}&type=${encodeURIComponent(type)}`,
                                        )
                                    }
                                >
                                    {type}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>

            {deleteTarget && (
                <ConfirmModal
                    title="Delete Transaction"
                    message="Delete this transaction? Stock will be reversed automatically."
                    onConfirm={handleDeleteTx}
                    onCancel={() => setDeleteTarget(null)}
                    loading={deleting}
                />
            )}
        </div>
    );
}
